import React from 'react';
import Modal from 'components/Modal';
import IconClose from 'components/svg/Close';

import classNames from 'classnames';
import NewsletterTile from 'components/Tiles/NewsletterTile';
import { DisplayLayout } from '../Collection.const';
import { contains, isEmpty } from 'util/index';
import { isMobile } from 'util/device';
import TileComponent from 'components/TileComponent';
import { createUseStyles } from 'react-jss';
import styles from './CollectionModal.styles';
import CollectionModalContext from './CollectionModalContext';

const iconCloseStyling = {
  width: 18,
  height: 18,
  display: 'block',
};

interface CollectionModalProps {
  contentTypeId: string;
  title: string;
  items: Array<{
    id: string;
    name: string;
    slug: string;
    type: string;
  }>;
  tileComponent: string;
  display: string;
  itemsPerRow: number;
  toggleCollectionModal: (val?: string) => void;
  isModalOpen: boolean;
  onError: (slug: string) => void;
}

const useStyles = createUseStyles(styles, { name: 'CollectionModal' });
const CollectionModal = ({
  title,
  items,
  tileComponent,
  display,
  itemsPerRow,
  toggleCollectionModal,
  isModalOpen,
  contentTypeId,
  ...rest
}: CollectionModalProps): JSX.Element => {
  const classes = useStyles();
  const container: unknown = null;

  const listViewSimple = display === DisplayLayout.LIST;
  const listViewGradient = contains(
    [DisplayLayout.LIST_NOW_PLAYING_HALF, DisplayLayout.LIST_NOW_PLAYING_FULL],
    display
  );
  const tileClass = classes[`tileView${itemsPerRow}Items`];

  const dismissModal = () => toggleCollectionModal('dismiss');
  const closeModal = () => toggleCollectionModal();

  return (
    <Modal shouldRender={isModalOpen} dismissFunction={dismissModal} container={container}>
      {isMobile && (
        <button type="button" onClick={closeModal} className={classes.closeOverlay}>
          <IconClose style={iconCloseStyling} /> esc
        </button>
      )}
      <div data-testid={`collection-modal-${contentTypeId}`} className={classes.collectionViewWrapper}>
        <section className={classes.collectionView}>
          {title && (
            <div className={classes.viewHeader}>
              <h2 className={classes.title}>{title}</h2>
              {!isMobile && (
                <button
                  type="button"
                  className={classes.closeButton}
                  onClick={closeModal}
                  data-testid="close_show_more"
                >
                  <IconClose style={iconCloseStyling} /> esc
                </button>
              )}
            </div>
          )}

          <div
            className={classNames(classes.collectionWrapper, classes.collectionModal, {
              [classes.collectionWrapperList]: listViewSimple || listViewGradient,
            })}
          >
            <div className={classes.collection}>
              {!isEmpty(items) &&
                items.map((tile, index) => (
                  <div
                    key={`${tile.slug || tile.id || tile.name}-${index}`}
                    className={classNames(
                      classes.collectionItem,
                      {
                        [classes.listViewGradientFullWidth]: display === DisplayLayout.LIST_NOW_PLAYING_FULL,
                        [classes.listViewGradientHalfWidth]: display === DisplayLayout.LIST_NOW_PLAYING_HALF,
                        [classes.listViewSimple]: listViewSimple,
                      },
                      [classes.tileView, tileClass]
                    )}
                  >
                    {tile.type === 'newsletterTile' ? (
                      <NewsletterTile contentTypeId={contentTypeId} {...tile} />
                    ) : (
                      <CollectionModalContext.Provider
                        value={{
                          isInModal: true,
                        }}
                      >
                        <TileComponent
                          tileComponent={tileComponent}
                          {...tile}
                          {...rest}
                          display={display}
                          index={index}
                        />
                      </CollectionModalContext.Provider>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </section>
      </div>
    </Modal>
  );
};

export default CollectionModal;
